import axios from "axios";
import AccountsService from "./accountsService";

export const CollateralSalesService = {
  // Record a collateral sale
  recordSale: async (saleData, token) => {
    try {
      const { collateral_id, sale_amount, account_id, related_loan_id, date_sold, summary, company_id } = saleData;
      
      // First, create the sale record
      const saleRecord = {
        collateral_id,
        sale_amount: parseFloat(sale_amount),
        initial_value: saleData.initial_value,
        related_loan_id,
        date_sold,
        summary,
        company: company_id,
        profit_loss: parseFloat(sale_amount) - parseFloat(saleData.initial_value),
        status: "Completed",
        created_at: new Date().toISOString(),
      };

      // Check if collateral_sales table exists, if not, we'll use a different approach
      let saleResponse;
      try {
        saleResponse = await axios.post(
          "https://zedlenders.pickmesms.com/items/collateral_sales",
          saleRecord,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } catch (error) {
        // If table doesn't exist, we'll store the sale info in the collateral record itself
        console.log("collateral_sales table not found, updating collateral record instead");
        await axios.patch(
          `https://zedlenders.pickmesms.com/items/collateral_register/${collateral_id}`,
          {
            status: "Sold",
            sale_amount: parseFloat(sale_amount),
            sale_date: date_sold,
            sale_summary: summary,
            related_loan_id,
            profit_loss: parseFloat(sale_amount) - parseFloat(saleData.initial_value),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Update collateral status to sold
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/collateral_register/${collateral_id}`,
        { status: "Sold" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Record transaction
      const transactionData = {
        amount: parseFloat(sale_amount),
        transaction_date: date_sold,
        company: company_id,
        to_account: account_id,
        is_loan_transaction: false,
        transaction_status: "Completed",
        transaction_type: "Collateral Sale",
        notes: `Sale of collateral. ${summary}`,
        payment_method: "collateral_sale",
        reference_number: `COLSALE-${Date.now()}`,
        collateral_id,
      };

      const transactionResponse = await axios.post(
        "https://zedlenders.pickmesms.com/items/transactions",
        transactionData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Update account balance
      const accountResponse = await axios.get(
        `https://zedlenders.pickmesms.com/items/accounts/${account_id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const currentBalance = parseFloat(accountResponse.data.data.balance) || 0;
      const newBalance = currentBalance + parseFloat(sale_amount);

      await axios.patch(
        `https://zedlenders.pickmesms.com/items/accounts/${account_id}`,
        { balance: newBalance },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return {
        sale: saleResponse?.data?.data,
        transaction: transactionResponse.data.data,
        success: true,
      };
    } catch (error) {
      console.error("Error recording collateral sale:", error);
      throw error;
    }
  },

  // Get all collateral sales
  getSales: async (companyId, token) => {
    try {
      // First try to get from collateral_sales table
      try {
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/collateral_sales?filter[company]=${companyId}&fields=*,collateral_id.*,related_loan_id.*`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return response.data.data;
      } catch (error) {
        // If table doesn't exist, get from collateral_register where status is Sold
        console.log("Getting sales from collateral_register table");
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/collateral_register?filter[company]=${companyId}&filter[status][_eq]=Sold&fields=*,loan.*`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return response.data.data.map(item => ({
          ...item,
          documents: typeof item.documents === 'string' ? 
            (() => {
              try {
                return JSON.parse(item.documents || '[]');
              } catch (e) {
                return [];
              }
            })() : 
            item.documents || [],
          sale_amount: item.sale_amount || 0,
          profit_loss: item.profit_loss || 0,
          date_sold: item.sale_date || item.date_created,
        }));
      }
    } catch (error) {
      console.error("Error fetching collateral sales:", error);
      throw error;
    }
  },

  // Get collateral available for sale (not sold yet)
  getAvailableForSale: async (companyId, token) => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/collateral_register?filter[company]=${companyId}&filter[status][_neq]=Sold&fields=*,loan.id,loan.amount,loan.borrower.first_name,loan.borrower.last_name`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data.data.map(item => ({
        ...item,
        documents: typeof item.documents === 'string' ? 
          (() => {
            try {
              return JSON.parse(item.documents || '[]');
            } catch (e) {
              return [];
            }
          })() : 
          item.documents || [],
      }));
    } catch (error) {
      console.error("Error fetching available collateral:", error);
      throw error;
    }
  },

  // Mark collateral as sold
  markAsSold: async (collateralId, token) => {
    try {
      const response = await axios.patch(
        `https://zedlenders.pickmesms.com/items/collateral_register/${collateralId}`,
        { status: "Sold" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error marking collateral as sold:", error);
      throw error;
    }
  },
};

export default CollateralSalesService;